import React, { useState, useRef, useEffect } from 'react';
import { Info } from 'lucide-react';
import { TermDefinition } from '../data/glossary-data';

interface TermTooltipProps {
  term: string;
  definition: TermDefinition;
  children: React.ReactNode;
}

const TermTooltip: React.FC<TermTooltipProps> = ({ definition, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const calculatePosition = () => {
    if (triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      // Use fixed positioning relative to viewport (works with any zoom level)
      let top = triggerRect.bottom + 8;
      let left = triggerRect.left;

      // Adjust if tooltip goes off screen to the right
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      if (left + tooltipRect.width > viewportWidth - 16) {
        left = Math.max(16, viewportWidth - tooltipRect.width - 16);
      }

      // Adjust if tooltip goes off screen to the left
      if (left < 16) {
        left = 16;
      }

      // Adjust if tooltip goes off screen at the bottom
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      if (top + tooltipRect.height > viewportHeight - 16) {
        // Position above the trigger instead
        top = triggerRect.top - tooltipRect.height - 8;
        // If still off screen, position at bottom of viewport
        if (top < 16) {
          top = viewportHeight - tooltipRect.height - 16;
        }
      }

      setPosition({ top, left });
    }
  };

  useEffect(() => {
    if (isVisible) {
      calculatePosition();
      
      // Recalculate on scroll and resize (handles zoom changes)
      window.addEventListener('scroll', calculatePosition, true);
      window.addEventListener('resize', calculatePosition);
      
      return () => {
        window.removeEventListener('scroll', calculatePosition, true);
        window.removeEventListener('resize', calculatePosition);
      };
    }
  }, [isVisible]);

  return (
    <>
      <span
        ref={triggerRef}
        className="relative inline-flex items-center group cursor-help"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
      >
        <span className="border-b-2 border-dotted border-primary-400 text-primary-700 font-medium">
          {children}
        </span>
        <Info className="w-3 h-3 ml-1 text-primary-500 opacity-70 group-hover:opacity-100 transition-opacity" />
      </span>

      {isVisible && (
        <div
          ref={tooltipRef}
          className="fixed z-50 w-80 bg-white rounded-lg shadow-2xl border-2 border-primary-200 p-4 animate-fadeIn"
          style={{ top: `${position.top}px`, left: `${position.left}px` }}
        >
          <div className="mb-2">
            <h4 className="font-bold text-gray-900 text-sm">{definition.term}</h4>
            {definition.aliases && definition.aliases.length > 0 && (
              <p className="text-xs text-gray-500 italic mt-1">
                Also: {definition.aliases.join(', ')}
              </p>
            )}
          </div>

          <p className="text-sm text-gray-700 leading-relaxed mb-3">
            {definition.definition}
          </p>

          {definition.formula && (
            <div className="bg-blue-50 rounded px-3 py-2 mb-2 border-l-2 border-blue-400">
              <p className="text-xs font-semibold text-blue-900 mb-1">Formula</p>
              <code className="text-xs text-blue-800 font-mono">{definition.formula}</code>
            </div>
          )}

          {definition.example && (
            <div className="bg-green-50 rounded px-3 py-2 border-l-2 border-green-400">
              <p className="text-xs font-semibold text-green-900 mb-1">Example</p>
              <p className="text-xs text-green-800">{definition.example}</p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default TermTooltip;
